/**
 * 流式编排 composable。
 *
 * <p>职责：作为对外暴露的流式操作门面，协调主对话流与 V2 待办恢复流。</p>
 *
 * <p>本文件保留两个主流程入口：</p>
 * <ul>
 *   <li>{@link sendMessage} - 主对话流入口</li>
 *   <li>{@link regenerateAssistantAfterUser} - 重新生成助手回复</li>
 * </ul>
 *
 * <p>以下职责已拆分至独立模块：</p>
 * <ul>
 *   <li>{@link ./streamRuntime} - 核心运行时工具（finalize / settle / bridge / cancel / 错误处理）</li>
 *   <li>{@link ./pendingResume} - V2 待办恢复流</li>
 * </ul>
 */
import {
  aiChatApi,
  postAiChatStream,
  postAiContinueRunStream,
  postAiRegenerateStream,
  type AiChatResponse
} from '@/api/aiChat'
import { useAiModelPreferenceStore } from '@/stores/aiModelPreference'
import { useAiSessionsStore } from '@/stores/aiSessions'
import { pushAiDebug } from '@/utils/aiDebugLog'
import type { AiChatContext, CancellableAbort } from './context'
import {
  AI_CHAT_FILE_ONLY_MESSAGE,
  genMessageId,
  type AiUiMessage
} from './types'
import {
  applyMissingDone,
  applyStreamError,
  applyUserCancelled,
  bindViewSessionIfCurrent,
  buildStreamBridge,
  cancelStream as cancelStreamRuntime,
  createPendingAssistantMessage,
  createStreamAccumulator,
  finalizeAssistantTurn,
  isViewingSession,
  settleStream,
  STREAM_INTERRUPTED_CONTINUE_HINT,
  withOwnerMessages,
  type BridgeDeps,
  type SettleDeps
} from './streamRuntime'
import {
  streamV2PendingResume,
  type PendingResumeDeps
} from './pendingResume'
import { createCoalescedDispatcher } from './streamCoalescer'
import { dispatchChatStreamEvent } from './streamEvents'
import { fetchSseV2Stream } from '@/api/aiChatV2'

export function useStreamOrchestrator(ctx: AiChatContext): {
  buildStreamBridge: (params: {
    pendingMsgId: string
    pendingMsgTemplate?: AiUiMessage
    sessionsStore: ReturnType<typeof useAiSessionsStore>
    hadPersistedSession: boolean
    abortCtrl: CancellableAbort
    acc: import('./types').ChatStreamAccumulator
    clientUserMsgId?: string | null
  }) => import('./streamEvents').ChatStreamBridge
  finalizeAssistantTurn: (pendingMsgId: string, data: AiChatResponse) => void
  sendMessage: (
    content: string,
    materialIds: string[],
    options?: { materialAttachments?: AiUiMessage['materialAttachments'] }
  ) => Promise<void>
  regenerateAssistantAfterUser: (
    userMessageId: number,
    assistantMsgId: string
  ) => Promise<void>
  continueRun: () => Promise<void>
  resumeConfirmation: (
    pendingConfirmationId: string,
    userChoice?: 'CONFIRM' | 'CANCEL',
    riskAcknowledged?: boolean
  ) => Promise<void>
  resumeUserQuestion: (
    pendingQuestionId: string,
    userChoice?: string,
    extraPayload?: Record<string, unknown>
  ) => Promise<void>
  cancelStream: () => void
} {
  const {
    sessionId,
    messages,
    lastError,
    recoverableStreamState,
    resumableStop,
    userActionQueue,
    streamsBySession,
    messagesBySession,
    aiPlanStore
  } = ctx

  // 为 buildStreamBridge / settleStream 构造静态依赖（响应式 ref 仍指向同一对象）
  const bridgeDeps: BridgeDeps = {
    messages,
    sessionId,
    userActionQueue,
    lastError,
    resumableStop,
    streamsBySession,
    aiPlanStore,
    messagesBySession
  }
  const settleDeps: SettleDeps = {
    streamsBySession,
    userActionQueue
  }

  /** 把服务端最终 done payload 同步到消息列表。 */
  function finalizeAssistantTurnLocal(
    pendingMsgId: string,
    data: AiChatResponse,
    ownerSessionId?: string | null
  ): void {
    withOwnerMessages(
      messages,
      messagesBySession,
      sessionId.value,
      ownerSessionId ?? data.sessionId,
      (target) => finalizeAssistantTurn({ messages: target }, pendingMsgId, data)
    )
  }

  function settleStreamFor(
    ownerSessionId: string | null | undefined,
    sessionsStore: ReturnType<typeof useAiSessionsStore>
  ): void {
    settleStream(settleDeps, ownerSessionId, sessionsStore)
  }

  /** 拼装 ChatStreamBridge。 */
  function buildStreamBridgeLocal(params: {
    pendingMsgId: string
    pendingMsgTemplate?: AiUiMessage
    sessionsStore: ReturnType<typeof useAiSessionsStore>
    hadPersistedSession: boolean
    abortCtrl: CancellableAbort
    acc: import('./types').ChatStreamAccumulator
    clientUserMsgId?: string | null
  }): import('./streamEvents').ChatStreamBridge {
    return buildStreamBridge(bridgeDeps, params)
  }

  /** 主对话流：用户发送一条消息后启动的流式请求。 */
  async function sendMessage(
    content: string,
    materialIds: string[] = [],
    options?: { materialAttachments?: AiUiMessage['materialAttachments'] }
  ): Promise<void> {
    const trimmed = content.trim()
    const hasMaterials = materialIds.length > 0
    // 守门：本会话已有在途流则不再发（防止重复触发），不影响其他会话的并行任务
    if ((!trimmed && !hasMaterials) || streamsBySession.has(sessionId.value)) {
      return
    }

    const sessionsStore = useAiSessionsStore()
    const hadPersistedSession =
      sessionId.value != null &&
      sessionId.value !== '' &&
      !sessionsStore.isDraft
    lastError.value = null
    recoverableStreamState.value = null
    resumableStop.value = null
    if (!sessionId.value?.trim()) {
      sessionId.value = `ai-${crypto.randomUUID()}`
    }
    sessionsStore.markRunning(sessionId.value)

    // Keep the existing plan checklist across "继续"/resume turns. Clearing it here
    // made the sidebar empty until the model happened to call update_plan again.
    // plan_snapshot SSE still upgrades the snapshot when the server pushes a newer version.

    const messageForApi = trimmed.length > 0 ? trimmed : AI_CHAT_FILE_ONLY_MESSAGE

    const userMsg: AiUiMessage = {
      id: genMessageId(),
      role: 'user',
      content: trimmed,
      materialAttachments:
        options?.materialAttachments && options.materialAttachments.length > 0
          ? options.materialAttachments
          : undefined,
      createdAt: Date.now()
    }
    const pendingMsg = createPendingAssistantMessage()
    messages.value = [...messages.value, userMsg, pendingMsg]

    const streamAcc = createStreamAccumulator()
    streamAcc.ownerSessionId = sessionId.value
    streamAcc.registeredSessionId = sessionId.value
    const abortCtrl: CancellableAbort = new AbortController()
    streamsBySession.register(sessionId.value, abortCtrl)

    // 性能债修复(热点1-4降频)：高频流式事件合帧应用，渲染频率与 token 频率解耦；
    // 交互/终态事件立即冲刷。bridge 保持逐事件构建（读最新会话状态）。
    const applyStreamEvent = (eventName: string, data: unknown): void => {
      dispatchChatStreamEvent(eventName, data, buildStreamBridgeLocal({
        pendingMsgId: pendingMsg.id,
        pendingMsgTemplate: pendingMsg,
        sessionsStore,
        hadPersistedSession,
        abortCtrl,
        acc: streamAcc,
        clientUserMsgId: userMsg.id
      }))
    }
    const dispatcher = createCoalescedDispatcher(applyStreamEvent)

    async function recoverV2Journal(): Promise<void> {
      const runId = streamAcc.v2RunId
      if (!runId || abortCtrl._userCancelled) return
      let recoveryError: Error | null = null
      await fetchSseV2Stream(runId, {
        fromSeq: (streamAcc.v2LastSeq ?? 0) + 1,
        signal: abortCtrl.signal,
        onEvent: dispatcher.onEvent,
        onError: (error) => {
          recoveryError = error
        }
      })
      dispatcher.flush()
      if (recoveryError) throw recoveryError
    }

    try {
      const modelPref = useAiModelPreferenceStore()
      const modelId = modelPref.selectedApiId?.trim()

      await postAiChatStream(
        {
          sessionId: sessionId.value,
          message: messageForApi,
          model: modelId || null,
          materialIds
        },
        {
          signal: abortCtrl.signal,
          onEvent: dispatcher.onEvent
        }
      )
      // 流结束后同步冲刷残余缓冲，再检查终态（幂等）。
      dispatcher.flush()

      if (!streamAcc.doneProcessed && !streamAcc.streamErrored && streamAcc.v2RunId) {
        await recoverV2Journal()
      }

      if (streamAcc.doneProcessed) {
        return
      }
      if (streamAcc.streamErrored) {
        sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || sessionId.value)
        return
      }
      const data = streamAcc.donePayload as AiChatResponse | null
      if (data === null) {
        withOwnerMessages(
          messages,
          messagesBySession,
          sessionId.value,
          streamAcc.ownerSessionId,
          (target) => applyMissingDone(target, lastError, pendingMsg.id, isViewingSession(sessionId, streamAcc.ownerSessionId))
        )
        sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || sessionId.value)
        return
      }

      if (data.sessionId) {
        streamAcc.ownerSessionId = data.sessionId
        streamsBySession.move(streamAcc.registeredSessionId ?? sessionId.value, data.sessionId)
        streamAcc.registeredSessionId = data.sessionId
        bindViewSessionIfCurrent(sessionId, data.sessionId)
        sessionsStore.registerServerSession(data.sessionId, data.conversationTitle ?? null, {
          activate: isViewingSession(sessionId, data.sessionId)
        })
      }
      if (!hadPersistedSession) {
        void sessionsStore.loadFromServer()
      }
      finalizeAssistantTurnLocal(pendingMsg.id, data, streamAcc.ownerSessionId)
    } catch (err: unknown) {
      if (!streamAcc.doneProcessed && !streamAcc.streamErrored && streamAcc.v2RunId && !abortCtrl._userCancelled) {
        try {
          await recoverV2Journal()
        } catch {
          // The original request error remains the visible error if recovery also fails.
        }
      }
      if (streamAcc.doneProcessed || streamAcc.streamErrored) {
        return
      }
      pushAiDebug('store', '🔴 sendMessage 流异常', { error: err instanceof Error ? err.message : err })
      if (!streamAcc.doneProcessed) {
        const userCancelled = abortCtrl._userCancelled === true
        const owner = streamAcc.ownerSessionId || sessionId.value
        withOwnerMessages(
          messages,
          messagesBySession,
          sessionId.value,
          owner,
          (target) => {
            if (userCancelled) {
              applyUserCancelled(target, pendingMsg.id)
            } else {
              applyStreamError(
                target,
                lastError,
                pendingMsg.id,
                pendingMsg,
                err,
                isViewingSession(sessionId, owner)
              )
            }
          }
        )
        if (!userCancelled) {
          sessionsStore.abandonSessionTitleGenerating(owner)
          // 页内断连闭环：服务端若已把运行标记为 INTERRUPTED/recoverable，置位恢复态，
          // 让工作区展示“点击恢复”横幅，而不是会话静默死亡。
          void markRecoverableIfInterrupted(owner)
        }
      }
    } finally {
      settleStreamFor(streamAcc.ownerSessionId, sessionsStore)
    }
  }

  /** 重新生成助手回复：基于已存在的 user 消息重新拉起流。 */
  async function regenerateAssistantAfterUser(
    userMessageId: number,
    assistantMsgId: string
  ): Promise<void> {
    const sid = sessionId.value?.trim() ?? ''
    // 守门：本会话已有在途流则不重新生成，不影响其他会话的并行任务
    if (!sid || streamsBySession.has(sid)) {
      return
    }
    const hadPersistedSession = true
    lastError.value = null
    const sessionsStore = useAiSessionsStore()

    const aidx = messages.value.findIndex((m) => m.id === assistantMsgId)
    if (aidx < 0) {
      sessionsStore.markEnded(sid)
      return
    }

    const pendingMsg = createPendingAssistantMessage()
    messages.value.splice(aidx, 1, pendingMsg)

    const streamAcc = createStreamAccumulator()
    streamAcc.ownerSessionId = sid
    streamAcc.registeredSessionId = sid
    const abortCtrl: CancellableAbort = new AbortController()
    streamsBySession.register(sid, abortCtrl)
    sessionsStore.markRunning(sid)

    // 性能债修复(热点1-4降频)：同 sendMessage，高频事件合帧应用。
    const applyRegenerateEvent = (eventName: string, data: unknown): void => {
      dispatchChatStreamEvent(eventName, data, buildStreamBridgeLocal({
        pendingMsgId: pendingMsg.id,
        pendingMsgTemplate: pendingMsg,
        sessionsStore,
        hadPersistedSession,
        abortCtrl,
        acc: streamAcc,
        clientUserMsgId: null
      }))
    }
    const dispatcher = createCoalescedDispatcher(applyRegenerateEvent)

    try {
      const modelPref = useAiModelPreferenceStore()
      const modelId = modelPref.selectedApiId?.trim()

      await postAiRegenerateStream(sid, userMessageId, {
        signal: abortCtrl.signal,
        model: modelId || null,
        onEvent: dispatcher.onEvent
      })
      // 流结束后同步冲刷残余缓冲，再检查终态（幂等）。
      dispatcher.flush()

      if (streamAcc.doneProcessed) {
        return
      }
      if (streamAcc.streamErrored) {
        sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || sessionId.value)
        return
      }
      const data = streamAcc.donePayload as AiChatResponse | null
      if (data === null) {
        withOwnerMessages(
          messages,
          messagesBySession,
          sessionId.value,
          streamAcc.ownerSessionId,
          (target) => applyMissingDone(target, lastError, pendingMsg.id, isViewingSession(sessionId, streamAcc.ownerSessionId))
        )
        sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || sessionId.value)
        return
      }

      if (data.sessionId) {
        streamAcc.ownerSessionId = data.sessionId
        streamsBySession.move(streamAcc.registeredSessionId ?? sessionId.value, data.sessionId)
        streamAcc.registeredSessionId = data.sessionId
        bindViewSessionIfCurrent(sessionId, data.sessionId)
        sessionsStore.registerServerSession(data.sessionId, data.conversationTitle ?? null, {
          activate: isViewingSession(sessionId, data.sessionId)
        })
      }
      finalizeAssistantTurnLocal(pendingMsg.id, data, streamAcc.ownerSessionId)
    } catch (err: unknown) {
      pushAiDebug('store', '🔴 regenerate 流异常', { error: err instanceof Error ? err.message : err })
      // 异常路径同样先冲刷残余缓冲，确保已到达的事件不丢在缓冲区里。
      dispatcher.flush()
      if (!streamAcc.doneProcessed) {
        const userCancelled = abortCtrl._userCancelled === true
        const owner = streamAcc.ownerSessionId || sessionId.value
        withOwnerMessages(
          messages,
          messagesBySession,
          sessionId.value,
          owner,
          (target) => {
            if (userCancelled) {
              applyUserCancelled(target, pendingMsg.id)
            } else {
              applyStreamError(
                target,
                lastError,
                pendingMsg.id,
                pendingMsg,
                err,
                isViewingSession(sessionId, owner)
              )
            }
          }
        )
        if (!userCancelled) {
          sessionsStore.abandonSessionTitleGenerating(owner)
          // 页内断连闭环：服务端若已把运行标记为 INTERRUPTED/recoverable，置位恢复态，
          // 让工作区展示“点击恢复”横幅，而不是会话静默死亡。
          void markRecoverableIfInterrupted(owner)
        }
      }
    } finally {
      settleStreamFor(streamAcc.ownerSessionId, sessionsStore)
    }
  }

  /** 流异常后探测服务端流态：运行已被安全停止（终态）时引导用户从断点续接。 */
  async function markRecoverableIfInterrupted(ownerSessionId: string | null): Promise<void> {
    const sid = ownerSessionId?.trim() ?? ''
    if (!sid) {
      return
    }
    try {
      const state = (await aiChatApi.getStreamState(sid)).data ?? null
      if (state?.recoverable && sessionId.value === sid) {
        // recoverable 恒为终态：没有可重连的活流，不给“点击恢复”按钮，
        // 直接告诉用户进度已保存、如何从断点续接。
        lastError.value = STREAM_INTERRUPTED_CONTINUE_HINT
      }
    } catch {
      // 服务端不可达时保留既有错误提示，不叠加额外噪音。
    }
  }

  /**
   * 继续回合：上一回合异常停止（步数上限/预算熔断/引擎错误）后，从停点续跑。
   * 不新增用户消息——后端从历史重建上下文，模型自然从停点继续。
   * 若本回合再次异常停止，重新置位 resumableStop（继续按钮常驻，直至正常完成）。
   */
  async function continueRun(): Promise<void> {
    const sid = sessionId.value?.trim() ?? ''
    // 守门：本会话已有在途流则不重复触发，不影响其他会话的并行任务
    if (!sid || streamsBySession.has(sid)) {
      return
    }
    const hadPersistedSession = true
    lastError.value = null
    resumableStop.value = null
    const sessionsStore = useAiSessionsStore()

    const pendingMsg = createPendingAssistantMessage()
    messages.value = [...messages.value, pendingMsg]

    const streamAcc = createStreamAccumulator()
    streamAcc.ownerSessionId = sid
    streamAcc.registeredSessionId = sid
    const abortCtrl: CancellableAbort = new AbortController()
    streamsBySession.register(sid, abortCtrl)
    sessionsStore.markRunning(sid)

    const applyContinueEvent = (eventName: string, data: unknown): void => {
      dispatchChatStreamEvent(eventName, data, buildStreamBridgeLocal({
        pendingMsgId: pendingMsg.id,
        pendingMsgTemplate: pendingMsg,
        sessionsStore,
        hadPersistedSession,
        abortCtrl,
        acc: streamAcc,
        clientUserMsgId: null
      }))
    }
    const dispatcher = createCoalescedDispatcher(applyContinueEvent)

    try {
      await postAiContinueRunStream(sid, {
        signal: abortCtrl.signal,
        onEvent: dispatcher.onEvent
      })
      dispatcher.flush()

      // 终态置位由 streamEvents reducer 统一处理（run_done/run_error 判定 finishReason），
      // 与 sendMessage / regenerate 共用同一逻辑，避免只在继续路径判定而漏掉首次触发。
      if (streamAcc.doneProcessed) {
        return
      }
      if (streamAcc.streamErrored) {
        sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || sid)
        return
      }
      const data = streamAcc.donePayload as AiChatResponse | null
      if (data === null) {
        withOwnerMessages(
          messages,
          messagesBySession,
          sessionId.value,
          streamAcc.ownerSessionId,
          (target) => applyMissingDone(target, lastError, pendingMsg.id, isViewingSession(sessionId, streamAcc.ownerSessionId))
        )
        sessionsStore.abandonSessionTitleGenerating(streamAcc.ownerSessionId || sid)
        return
      }

      if (data.sessionId) {
        streamAcc.ownerSessionId = data.sessionId
        streamsBySession.move(streamAcc.registeredSessionId ?? sessionId.value, data.sessionId)
        streamAcc.registeredSessionId = data.sessionId
        bindViewSessionIfCurrent(sessionId, data.sessionId)
      }
      finalizeAssistantTurnLocal(pendingMsg.id, data, streamAcc.ownerSessionId)
    } catch (err: unknown) {
      pushAiDebug('store', '🔴 continueRun 流异常', { error: err instanceof Error ? err.message : err })
      dispatcher.flush()
      if (!streamAcc.doneProcessed) {
        const userCancelled = abortCtrl._userCancelled === true
        const owner = streamAcc.ownerSessionId || sid
        withOwnerMessages(
          messages,
          messagesBySession,
          sessionId.value,
          owner,
          (target) => {
            if (userCancelled) {
              applyUserCancelled(target, pendingMsg.id)
            } else {
              applyStreamError(
                target,
                lastError,
                pendingMsg.id,
                pendingMsg,
                err,
                isViewingSession(sessionId, owner)
              )
            }
          }
        )
        if (!userCancelled) {
          sessionsStore.abandonSessionTitleGenerating(owner)
          void markRecoverableIfInterrupted(owner)
        }
      }
    } finally {
      settleStreamFor(streamAcc.ownerSessionId, sessionsStore)
    }
  }

  /** 确认后恢复：委托给 pendingResume 模块。 */
  async function resumeConfirmation(
    pendingConfirmationId: string,
    userChoice: 'CONFIRM' | 'CANCEL' = 'CONFIRM',
    riskAcknowledged = false
  ): Promise<void> {
    const deps: PendingResumeDeps = {
      sessionId,
      messages,
      lastError,
      resumableStop,
      streamsBySession,
      messagesBySession,
      aiPlanStore,
      userActionQueue
    }
    return streamV2PendingResume(
      deps,
      pendingConfirmationId,
      'CONFIRMATION',
      userChoice,
      { riskAcknowledged }
    )
  }

  /** 提问后恢复：委托给 pendingResume 模块。批量提问组的整组答案经 extraPayload.groupAnswers 透传。 */
  async function resumeUserQuestion(
    pendingQuestionId: string,
    userChoice = '',
    extraPayload?: Record<string, unknown>
  ): Promise<void> {
    const deps: PendingResumeDeps = {
      sessionId,
      messages,
      lastError,
      resumableStop,
      streamsBySession,
      messagesBySession,
      aiPlanStore,
      userActionQueue
    }
    return streamV2PendingResume(deps, pendingQuestionId, 'QUESTION', userChoice, extraPayload)
  }

  /** 用户主动停止生成（仅停止当前查看会话的流，不影响其他会话的并行任务）。 */
  function cancelStream(): void {
    cancelStreamRuntime(streamsBySession, sessionId)
  }

  return {
    buildStreamBridge: buildStreamBridgeLocal,
    finalizeAssistantTurn: finalizeAssistantTurnLocal,
    sendMessage,
    regenerateAssistantAfterUser,
    continueRun,
    resumeConfirmation,
    resumeUserQuestion,
    cancelStream
  }
}
